/**
 * React-Query hooks for `/api/screeners/*` (PULSE-268, Screeners v2).
 *
 * Каталог скринеров (defs, admin управляет видимостью/назначениями) +
 * юзерские инстансы (каждый со своими params/chat_id/cooldown) +
 * лента срабатываний с forward-return'ами.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

// ─── Types ───────────────────────────────────────────────────────────

export type Visibility = 'off' | 'public' | 'assigned'
export type MetricDirection =
  'pump' | 'dump' | 'flat' | 'up' | 'down' | 'long' | 'short' | 'any' | 'pos' | 'neg'

// PULSE-379: фильтры монет и рынка поверх условий
export interface ScreenerFilters {
  min_turnover_m?: number
  min_oi_m?: number
  symbols_only?: string[]
  symbols_exclude?: string[]
  btc?: { enabled: boolean; direction: string; window_min: number; change_pct: number }
  hours?: { enabled: boolean; from: number; to: number }
}

export interface MetricParams {
  enabled: boolean
  direction?: MetricDirection
  window_min?: number
  change_pct?: number
  // только для nd (PULSE-268): режим порога
  mode?: 'pct' | 'usd'
  threshold_usd?: number
}

export interface UniversalParams {
  price?: MetricParams
  oi?: MetricParams
  nd?: MetricParams
  gap?: MetricParams
  vol?: MetricParams
  liq?: MetricParams
  fund?: MetricParams
  filters?: ScreenerFilters
  [k: string]: unknown
}

export interface SignalFilters {
  direction?: string
  symbol?: string
  symbols?: string[]
  min_score?: number
  min_dp_pct?: number
  template?: string
  [k: string]: unknown
}

export interface ScreenerDef {
  id: number
  key: string
  kind: 'universal' | 'signal'
  signal_type: string | null
  name: string
  description: string
  visibility: Visibility
  default_params: UniversalParams
  assigned_user_ids: number[] | null
  instances_total: number | null
  instances_enabled: number | null
}

export interface Instance {
  id: number
  user_id: number
  screener_id: number
  label: string
  enabled: boolean
  params: UniversalParams & SignalFilters
  chat_id: string
  cooldown_min: number
  sent_count: number
  last_sent_at: string | null
  user_email?: string | null
}

export interface AvailableScreener {
  def: ScreenerDef
  instances: Instance[]
}

export interface ScreenersOverview {
  available: AvailableScreener[]
  is_admin: boolean
}

export interface InstanceUpsert {
  label?: string
  enabled: boolean
  params: UniversalParams & SignalFilters
  chat_id: string
  cooldown_min: number
}

export interface DefCreate {
  key: string
  kind: 'universal' | 'signal'
  signal_type?: string | null
  name: string
  description?: string
  visibility?: Visibility
}

export type DefUpdate = Partial<Omit<DefCreate, 'key' | 'kind'>>

export interface SignalRow {
  id: number
  ts: string
  screener_id: number
  screener_key: string
  symbol: string
  config_hash: string
  params: Record<string, unknown>
  metrics: Record<string, number | string>
  price: number | null
  source_alert_id: number | null
  fwd_15m_pct: number | null
  fwd_1h_pct: number | null
  fwd_4h_pct: number | null
  // название моего скринера, которому ушёл сигнал (PULSE-371)
  label?: string | null
}

export interface SignalsResponse { signals: SignalRow[] }
export interface SignalTypesResponse { signal_types: string[] }
export interface TestSendResponse { ok: boolean; error: string | null }

// ─── Queries ─────────────────────────────────────────────────────────

// ─── Подбор сетапов (PULSE-372) ──────────────────────────────────────

export interface SetupRun {
  id: number
  finished_at: string
  data_from: string
  data_to: string
  split_ts: string
  n_cells: number
  n_variants: number
  fee_pct: number
  refire_min: number
  // фон: средний нетто входа в любую минуту; [часть][холд][сторона]
  baseline: Record<'fit' | 'check', Record<string, { long: number; short: number }>> | null
}

export interface SetupFamily {
  family: string
  n_variants: number
  best_t_fit: number | null
}

export interface SetupRow {
  family: string
  params: UniversalParams
  side: 'long' | 'short'
  hold_min: number
  n: number
  per_day: number
  mean_net: number
  median_net: number
  wr: number
  t_day: number | null
  n_symbols: number
  top_symbol_share: number
  n_fit: number
  per_day_fit: number
  mean_fit: number | null
  wr_fit: number | null
  t_fit: number | null
  n_check: number
  per_day_check: number
  mean_check: number | null
  wr_check: number | null
  t_check: number | null
}

export interface SetupsResponse {
  run: SetupRun | null
  families: SetupFamily[]
  rows: SetupRow[]
}

export function useScreenerSetups(family: string | null) {
  return useQuery({
    queryKey: ['screener-setups', family],
    queryFn: async () => {
      const { data } = await api.get<SetupsResponse>('/screeners/setups', {
        params: family ? { family } : {},
      })
      return data
    },
    staleTime: 5 * 60_000,
  })
}

// «Свой вариант»: заявка в очередь, результат через 1–2 мин
export interface SetupRequest {
  id: number
  params: UniversalParams
  status: 'queued' | 'running' | 'done' | 'error'
  created_at: string
  finished_at: string | null
  error: string | null
  queue_pos: number
  result: {
    rows: SetupRow[]
    n_events: number
    data_from: string
    data_to: string
    split_ts: string
    baseline: SetupRun['baseline']
  } | null
}

export function useSetupRequests() {
  return useQuery({
    queryKey: ['screener-setup-requests'],
    queryFn: async () => {
      const { data } = await api.get<{ requests: SetupRequest[] }>('/screeners/setups/custom')
      return data.requests
    },
    // пока что-то считается — опрашиваем
    refetchInterval: (q) =>
      (q.state.data ?? []).some((r) => r.status === 'queued' || r.status === 'running') ? 4000 : false,
  })
}

export function useCreateSetupRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (params: UniversalParams) => {
      const { data } = await api.post<SetupRequest>('/screeners/setups/custom', { params })
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['screener-setup-requests'] }),
  })
}

export function useScreenersOverview() {
  return useQuery({
    queryKey: ['screeners'],
    queryFn: async () => {
      const { data } = await api.get<ScreenersOverview>('/screeners')
      return data
    },
    staleTime: 10_000,
  })
}

export interface SignalsQuery {
  screenerId: number
  limit?: number
  userId?: number          // 0 = все (админ может фильтровать по юзеру)
  sort?: 'ts' | 'fwd15' | 'fwd1h' | 'fwd4h'
  order?: 'asc' | 'desc'
}

export function useScreenerSignals(q: SignalsQuery) {
  return useQuery({
    queryKey: ['screeners', 'signals', q],
    queryFn: async () => {
      const { data } = await api.get<SignalsResponse>('/screeners/signals', {
        params: {
          screener_id: q.screenerId, limit: q.limit ?? 100,
          user_id: q.userId ?? 0, sort: q.sort ?? 'ts', order: q.order ?? 'desc',
        },
      })
      return data
    },
    staleTime: 15_000,
    refetchInterval: 60_000,
  })
}

export function useSignalTypes(enabled: boolean) {
  return useQuery({
    queryKey: ['screeners', 'signal-types'],
    queryFn: async () => {
      const { data } = await api.get<SignalTypesResponse>('/screeners/signal-types')
      return data
    },
    staleTime: 60_000,
    enabled,
  })
}

// ─── Instance mutations (user) ───────────────────────────────────────

function useInvalidate() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: ['screeners'] })
}

export function useCreateInstance() {
  const inv = useInvalidate()
  return useMutation({
    mutationFn: async ({ screenerId, body }: { screenerId: number; body: InstanceUpsert }) => {
      const { data } = await api.post<Instance>(`/screeners/my/${screenerId}`, body)
      return data
    },
    onSuccess: inv,
  })
}

export function useUpdateInstance() {
  const inv = useInvalidate()
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: InstanceUpsert }) => {
      const { data } = await api.put<Instance>(`/screeners/my/instance/${id}`, body)
      return data
    },
    onSuccess: inv,
  })
}

export function useDeleteInstance() {
  const inv = useInvalidate()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/screeners/my/instance/${id}`)
    },
    onSuccess: inv,
  })
}

export function useTestInstance() {
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<TestSendResponse>(`/screeners/my/instance/${id}/test`)
      return data
    },
  })
}

// ─── Backtest (PULSE-268 ч.2) ────────────────────────────────────────

export interface BacktestEvent {
  ts: string
  symbol: string
  price: number | null
  metrics: Record<string, number | string | null>
}

export interface BacktestResponse {
  events: BacktestEvent[]
  count: number
}

export function useScreenerBacktest() {
  return useMutation({
    mutationFn: async ({ screenerId, params, days = 7, refireMin = 60 }: {
      screenerId: number
      params: Record<string, unknown>
      days?: number
      refireMin?: number
    }) => {
      const { data } = await api.post<BacktestResponse>(
        `/screeners/${screenerId}/backtest`,
        { params, days, refire_min: refireMin },
        { timeout: 180_000 },
      )
      return data
    },
  })
}

export interface EventChart {
  series: unknown          // MarketSeries — скармливается MarketChart как data
  signal_marker: { t: number; price: number } | null
  tf: string
  window: string
}

export function useEventChart(
  symbol: string | null, ts: string | null, window: string, tf?: string,
) {
  return useQuery({
    queryKey: ['screeners', 'event-chart', symbol, ts, window, tf ?? 'auto'],
    enabled: !!symbol && !!ts,
    queryFn: async () => {
      const { data } = await api.get<EventChart>('/screeners/backtest/chart', {
        params: { symbol, ts, window, ...(tf && tf !== 'auto' ? { tf } : {}) },
      })
      return data
    },
    staleTime: 5 * 60_000,
    retry: false,
  })
}

// ─── Admin mutations ─────────────────────────────────────────────────

export function useCreateDef() {
  const inv = useInvalidate()
  return useMutation({
    mutationFn: async (body: DefCreate) => {
      const { data } = await api.post<ScreenerDef>('/screeners/admin/defs', body)
      return data
    },
    onSuccess: inv,
  })
}

export function useUpdateDef() {
  const inv = useInvalidate()
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: DefUpdate }) => {
      const { data } = await api.put<ScreenerDef>(`/screeners/admin/defs/${id}`, body)
      return data
    },
    onSuccess: inv,
  })
}

export function useDeleteDef() {
  const inv = useInvalidate()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/screeners/admin/defs/${id}`)
    },
    onSuccess: inv,
  })
}

export function useSetDefAccess() {
  const inv = useInvalidate()
  return useMutation({
    mutationFn: async ({ id, userIds }: { id: number; userIds: number[] }) => {
      await api.put(`/screeners/admin/defs/${id}/access`, { user_ids: userIds })
    },
    onSuccess: inv,
  })
}
